import I18n from 'I18n';
import { isMultienum, isEnum, isDate, isPercent, isPercentWholeNumber, isProbability, isDuration, isDurationEquation, isFormattedNumber } from 'customer-data-objects/property/PropertyIdentifier';
import { DATE_TIME } from 'customer-data-objects/property/PropertyTypes'; // @ts-expect-error Legacy code isn't typed

import { propertyLabelTranslatorWithIsHubSpotDefined } from 'property-translator/propertyTranslator';
import { parseMultiEnumValue } from './parseMultiEnumValue';
import { EMPTY_PROPERTY_VALUE } from './constants/EmptyPropertyValue';
import PropertyRecord from 'customer-data-objects/property/PropertyRecord';
import { getRelativeDateTimeLabel } from './utils/getRelativeDateTimeLabel'; // We can't use `UISelectProps['options']` because this lib does not have a dependency on UIComponents
// See https://git.hubteam.com/HubSpot/UIComponents/blob/master/UIComponents/static/js/input/UISelect.d.ts

function indexBy(keyMapper) {
  return subject => subject.reduce((acc, v) => {
    acc[keyMapper(v)] = v;
    return acc;
  }, {});
}

const indexChoices = indexBy(c => c.id || c.value); // TODO: is `id` ever actually used? we have to cast to 'as any' here because `id` isn't a legal field on a UISelect option anyway

const isPropertyRecord = property => property instanceof PropertyRecord;

const toOptions = property => {
  const translatedPropertyOptions = property.options.map(option => {
    return {
      text: propertyLabelTranslatorWithIsHubSpotDefined({
        label: option.label,
        isHubSpotDefined: property.hubspotDefined
      }),
      value: option.value
    };
  });
  return Array.isArray(translatedPropertyOptions) ? translatedPropertyOptions : translatedPropertyOptions.toJS();
}; //if the dropdown is allowed to be cleared, use makeOptionsFromProperty


export function makeOptionsFromPropertyWithoutBlankOptions(property) {
  return toOptions(property);
}
export function makeOptionsFromProperty(property) {
  let options;
  const placeholder = propertyLabelTranslatorWithIsHubSpotDefined({
    label: isPropertyRecord(property) ? property.placeholder : '',
    isHubSpotDefined: property.hubspotDefined
  });
  const optionsArrFromProperties = toOptions(property);
  const hasBlankOption = property.options.some(option => option.value === '');

  if (!hasBlankOption) {
    const placeholderOption = {
      text: placeholder,
      value: ''
    };
    options = [placeholderOption, ...optionsArrFromProperties];
  } else {
    options = optionsArrFromProperties;
  }

  return options;
}
export function getDisplayedValue(property, value, choices = undefined, {
  dateFormat = 'L',
  dateTimeFormat = 'L LT z',
  relativeDate = false,
  relativeDateTime = false
} = {}) {
  let displayValue;

  if (value == null) {
    value = '';
  }

  const propIsMultienum = isMultienum(property);

  if (isEnum(property) || propIsMultienum) {
    const values = propIsMultienum ? parseMultiEnumValue(value) : [value]; // TODO: is `choices` ever actually passed in?

    if (choices == null) {
      choices = makeOptionsFromProperty(property);
    }

    const choiceMap = indexChoices(choices);
    displayValue = values.map(val => {
      if (val == null) {
        return val;
      }

      const option = choiceMap[val];
      return option ? option.text || val : val;
    }).join(', ');
  } else if (isDate(property) && value !== '' && value != null) {
    if (property.type === DATE_TIME) {
      const maybeRelativeLabel = relativeDateTime ? getRelativeDateTimeLabel({
        value,
        isDateTime: true
      }) : null;
      displayValue = maybeRelativeLabel || I18n.moment.userTz(Number(value)).format(dateTimeFormat);
    } else {
      // property.type === DATE
      const maybeRelativeLabel = relativeDate ? getRelativeDateTimeLabel({
        value,
        isDateTime: false
      }) : null;
      displayValue = maybeRelativeLabel || I18n.moment.utc(Number(value)).format(dateFormat);
    }
  } else if (isPercent(property) || isProbability(property)) {
    if (value === '') {
      displayValue = EMPTY_PROPERTY_VALUE;
    } else {
      displayValue = I18n.formatPercentage(Number(value) * 100, {
        precision: 2
      });
    }
  } else if (isPercentWholeNumber(property)) {
    if (value === '') {
      displayValue = EMPTY_PROPERTY_VALUE;
    } else {
      displayValue = I18n.formatPercentage(Number(value), {
        precision: 2
      });
    }
  } else if (isDuration(property) || isDurationEquation(property)) {
    // See `PropertyInputReadOnlyDuration`
    if (value === null || value === undefined || isNaN(+value) || value === '') {
      displayValue = EMPTY_PROPERTY_VALUE;
    } else {
      const from = I18n.moment().valueOf() - +value;
      const fromValue = I18n.moment.portalTz(from);
      displayValue = fromValue.toNow(true);
    }
  } else if (isFormattedNumber(property)) {
    // See `PropertyInputNumber`
    displayValue = value === '' ? '' : I18n.formatNumber(Number(value), {
      precision: 5
    });
  }

  return displayValue || value;
}